import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import { AbstractControl, UntypedFormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';

import { PolicyAnalytics } from '@amfam/policy/models';
import { AnalyticsFacade } from '@amfam/shared/analytics';
import { UserState } from '@amfam/shared/user';
import { Agent } from '@amfam/shared/utility/agent';
import { ImpersonateRolesService } from '@amfam/shared/utility/impersonation';
import { DsModalService } from '@amfam/ui-kit';

@Component({
  selector: 'ds-grace-period',
  templateUrl: './grace-period.component.html',
  styleUrls: ['./grace-period.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class GracePeriodComponent implements OnInit, OnDestroy, OnChanges {
  public static readonly errorModalId = 'PGPModal-Success';
  public static readonly gracePeriodModalId = 'PGPModal';
  public static readonly successModalId = 'PGPModal-Error';
  static readonly advancedGraceDays = '14';
  static readonly classicGraceDays = '30';

  @Input() activeAgents: Agent[];

  @Input() user: UserState;

  @Input() gracePeriodDays: string;

  @Input() isPartner: boolean;

  @Output() visibleChange = new EventEmitter<boolean>();

  public agents: Agent[] = [];
  public error = false;
  public errorModal: string;
  public gracePeriodModal: string;
  public modalWidth = '596';
  public producers: Agent[] = [];
  public successModal: string;
  public selectedAgent: Agent;
  public success = false;

  public agentNotificationForm: UntypedFormGroup;
  public selectedAgentInput: AbstractControl;
  public displayAgentSection = false;

  private stop$: Subject<void> = new Subject<void>();
  private _visible = false;

  constructor(
    private modalService: DsModalService,
    public roleService: ImpersonateRolesService,
    private route: Router,
    private analyticsFacade: AnalyticsFacade
  ) {}

  get visible(): boolean {
    return this._visible;
  }

  @Input()
  set visible(value: boolean) {
    this._visible = value;
    this.visibleChange.emit(this._visible);
  }

  @Input() set isAdvancedCustomer(value: boolean) {
    this.gracePeriodDays = value
      ? GracePeriodComponent.advancedGraceDays
      : GracePeriodComponent.classicGraceDays;
  }

  ngOnInit(): void {
    /**
     * AS: Unique modal ids for policy grace period modal.
     */
    this.gracePeriodModal = this.modalService.createModalId(
      GracePeriodComponent.gracePeriodModalId
    );
  }

  ngOnChanges(): void {
    if (this.visible) {
      this.open();
    } else {
      this.close();
    }
  }

  /**
   * AS:
   * @param _initialized : Boolean telling if the modal is up and running.
   * @function modalInitialized : Gets called once the modal has been initialized.
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  modalInitialized(_initialized: boolean) {}

  open(): void {
    this.modalService.open(this.gracePeriodModal);

    // TODO - needs to come up with a pattern to implement this without checking harcoded urls inside this component.
    if (
      this.route.url
        ?.toLocaleLowerCase()
        .includes('policies/risk-selection/replace-vehicle-quick-quote')
    ) {
      this.analyticsFacade.trackPage(PolicyAnalytics.pagePolicyGraceNotice);
    } else if (this.route.url?.toLocaleLowerCase().includes('policies/risk-selection')) {
      this.analyticsFacade.trackPage(
        PolicyAnalytics.pagePolicyAddVehicleNewOrReplaceVehicleGraceNotification
      );
    }
  }

  close(): void {
    this.modalService.close(this.gracePeriodModal);
    this.visible = false;
  }

  ngOnDestroy(): void {
    this.stop$.next();
    this.stop$.complete();
  }
}
